import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, createRef, useEffect, useRef } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router";

import { EmailLetter, Reply, World } from "@livechat/design-system-icons";
import { Icon, SpacingToken, Tab, TabsList, TabsWrapper } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SettingsSection } from "constants/settings/sections";
import useSettingsSection from "hooks/settings/use-settings-section";
import { withVar } from "themes/helpers";

import ViewContainer from "../ViewContainer";
import { SettingsContent } from "../styled";

import { DomainsSection } from "./domains/DomainsSection";
import { ForwardingAddressesSection } from "./forwarding-addresses/ForwardingAddressesSection";
import { ReplyAddressesSection } from "./reply-addresses/ReplyAddressesSection";
import { EmailSettingsRoutes, EmailSettingsSubsection } from "./routes";

const RouteMap = {
  [EmailSettingsSubsection.ForwardingAddresses]: EmailSettingsRoutes.ForwardingAddresses,
  [EmailSettingsSubsection.ReplyAddresses]: EmailSettingsRoutes.ReplyAddresses,
  [EmailSettingsSubsection.Domains]: EmailSettingsRoutes.Domains,
};

const SubsectionTabs = [
  {
    id: EmailSettingsSubsection.ForwardingAddresses,
    title: "Forwarding addresses",

    icon: <Icon source={EmailLetter} />,
  },
  {
    id: EmailSettingsSubsection.ReplyAddresses,
    title: "Reply addresses",
    icon: <Icon source={Reply} />,
  },
  {
    id: EmailSettingsSubsection.Domains,
    title: "Domains",
    icon: <Icon source={World} />,
  },
];

export const Email: FC = () => {
  const history = useHistory();

  const tabsRef = useRef({
    [EmailSettingsSubsection.ForwardingAddresses]: createRef<HTMLDivElement>(),
    [EmailSettingsSubsection.ReplyAddresses]: createRef<HTMLDivElement>(),
    [EmailSettingsSubsection.Domains]: createRef<HTMLDivElement>(),
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onTabClick = (id: EmailSettingsSubsection) => {
    history.push(RouteMap[id]);
  };

  useSettingsSection(SettingsSection.EmailAddresses);

  const currentPath = history.location.pathname;
  const currentId = Object.keys(RouteMap).find(key => RouteMap[key as EmailSettingsSubsection] === currentPath);

  useEffect(() => {
    if (currentId && tabsRef.current[currentId as EmailSettingsSubsection]) {
      tabsRef.current[currentId as EmailSettingsSubsection].current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentId]);

  return (
    <ViewContainer
      title="Email"
      contentCss={css`
        overflow: auto;
      `}
    >
      <SettingsContent>
        <TabsWrapper>
          <ScrollingTabsList>
            {SubsectionTabs.map(({ id, title, icon }) => (
              <Tab
                key={id}
                title={title}
                isSelected={currentId === id}
                onClick={() => onTabClick(id as EmailSettingsSubsection)}
                data-testid={`email-settings-tab-${id}`}
              >
                <Flex gap={withVar(SpacingToken.Spacing1)} ref={tabsRef.current[id]}>
                  {icon}
                  {title}
                </Flex>
              </Tab>
            ))}
          </ScrollingTabsList>
        </TabsWrapper>
        <Switch>
          <Route exact path={EmailSettingsRoutes.BaseRoute} component={EmailSettingsRedirect} />
          <Route path={EmailSettingsRoutes.ForwardingAddresses} component={ForwardingAddressesSection} />
          <Route path={EmailSettingsRoutes.ReplyAddresses} component={ReplyAddressesSection} />
          <Route path={EmailSettingsRoutes.Domains} component={DomainsSection} />
          <Redirect to={EmailSettingsRoutes.ForwardingAddresses} />
        </Switch>
      </SettingsContent>
    </ViewContainer>
  );
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const EmailSettingsRedirect = () => {
  const history = useHistory();

  useEffect(() => {
    history.replace(EmailSettingsRoutes.ForwardingAddresses);
  }, [history]);

  return null;
};

const ScrollingTabsList = styled(TabsList)`
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
`;
